import { useRef } from "react";
import { Navigate, Outlet, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/auth.context";
import { SessionLoadingFallback } from "@/components/auth/SessionLoadingFallback";
import {
  ADMIN_IMPERSONATION_TOKEN_PARAM,
  CHANGE_PASSWORD_ROUTE,
  POST_LOGIN_ROUTE,
} from "@/lib/auth-routes";

type Decisao = "redirecionar" | "exibir";

/**
 * Guard das rotas de visitante (tela de login).
 *
 * Quem já tem sessão válida ao abrir `/login` — caso típico de "Manter
 * Conectado" + favorito/link para a tela de login — vai direto para a área
 * logada, em vez de ver o formulário e digitar as credenciais de novo.
 *
 * A decisão é tomada UMA vez, assim que o AuthProvider termina de validar a
 * sessão armazenada (latch de inicialização em ref): o `login()` do contexto
 * continua dono da navegação pós-login (ele resolve o período orçamentário
 * antes de navegar), então o guard não reage a uma autenticação feita com a
 * tela já aberta.
 *
 * Exceção: o fluxo "acessar como assinante" do painel admin chega em
 * `/login?admin_impersonation_token=…` e precisa da LoginPage montada para
 * trocar o token — redirecionar antes descartaria o token.
 */
const GuestRoute = () => {
  const { isAuthenticated, loading, user } = useAuth();
  const [searchParams] = useSearchParams();

  // Mesmo critério da LoginPage: parâmetro vazio não inicia a troca de token.
  const fluxoImpersonacao = Boolean(searchParams.get(ADMIN_IMPERSONATION_TOKEN_PARAM));
  const sessaoAtiva = isAuthenticated && !fluxoImpersonacao;

  // Inicialização única do ref durante o render (exceção prevista pelo React):
  // fixa a decisão no primeiro render em que a sessão já foi validada.
  const decisao = useRef<Decisao | null>(null);
  if (!loading && decisao.current === null) {
    decisao.current = sessaoAtiva ? "redirecionar" : "exibir";
  }

  if (decisao.current === null) {
    return <SessionLoadingFallback />;
  }

  if (decisao.current === "redirecionar") {
    // Senha provisória vai direto para a troca, sem passar pelo ProtectedRoute.
    const destino = user?.must_change_password ? CHANGE_PASSWORD_ROUTE : POST_LOGIN_ROUTE;
    return <Navigate to={destino} replace />;
  }

  return <Outlet />;
};

export default GuestRoute;
